import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Plus, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { UploadPicture } from '../../features/UploadPicture/index.js';
import { ContentEditor } from '../../features/ContentEditor/ContentEditor.jsx';
import { LocalizationsBlock, validateLocalizations } from '../../features/LocalizationsBlock';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import {
  buildEntityFormData,
  mapLocalizationsFromApi,
} from '../../shared/utils/buildEntityFormData.js';

const CONTENT_KEYS = ['detailContent'];
const CV_ACCEPT = '.pdf,.doc,.docx';

const LOCALIZATION_FIELDS = [
  { name: 'name', label: 'Имя', type: 'text', required: true },
  { name: 'surname', label: 'Фамилия', type: 'text', required: true },
  { name: 'position', label: 'Должность', type: 'text' },
  { name: 'shortExpierence', label: 'Опыт', type: 'text' },
  { name: 'quote', label: 'Цитата', type: 'textarea' },
  {
    name: 'skills',
    label: 'Навыки',
    type: 'list',
    itemLabel: 'Навык',
    hint: 'Полный список на языке перевода',
  },
  {
    name: 'detailContent',
    label: 'Подробно о сотруднике',
    type: 'content',
    hint: 'Пустой контент — на сайте покажется русская версия.',
  },
  {
    name: 'cvFilepath',
    label: 'Резюме',
    type: 'file',
    formField: 'cvFile',
    accept: CV_ACCEPT,
    clearable: false,
    hint: 'Необязательно. Без своего резюме на сайте отдаётся основное.',
  },
  { name: 'seoMetatags', label: 'SEO', type: 'seo' },
];

const emptyForm = {
  name: '',
  surname: '',
  position: '',
  shortExpierence: '',
  quote: '',
  imgPath: '',
  cvFilepath: '',
  skills: [],
  detailContent: { items: [] },
  seoMetatags: { title: '', description: '', keywords: '' },
};

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Создание и редактирование участника команды: карточка, навыки, подробный рассказ, резюме, SEO и локализации — одним запросом
 */
const TeamMemberFormPage = () => {
  const { structureId, id: groupId, memberId } = useParams();
  const isEdit = Boolean(memberId);
  const navigate = useNavigate();
  const backTo = `/team-structures/${structureId}/group/${groupId}`;

  const [form, setForm] = useState(isEdit ? null : emptyForm);
  const [localizations, setLocalizations] = useState([]);
  const [mainImage, setMainImage] = useState(null);
  const [cvFile, setCvFile] = useState(null);
  const [skillInput, setSkillInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    apiRequests.team
      .getMember(groupId, memberId)
      .then((member) => {
        setForm({
          id: member.id,
          name: member.name || '',
          surname: member.surname || '',
          position: member.position || '',
          shortExpierence: member.shortExpierence || '',
          quote: member.quote || '',
          imgPath: member.imgPath || '',
          cvFilepath: member.cvFilepath || '',
          skills: (member.skills || []).map((s) => ({ title: s.title || '' })),
          detailContent: { items: member.detailContent?.items || [] },
          seoMetatags: member.seoMetatags || emptyForm.seoMetatags,
        });
        setLocalizations(
          mapLocalizationsFromApi(member.localizations, CONTENT_KEYS).map((l) => ({
            ...l,
            detailContent: l.detailContent || { items: [] },
            skills: (l.skills || []).map((s) => ({ title: s.title || '' })),
          })),
        );
      })
      .catch((error) => toast.error(getErrorMessage(error, 'Не удалось загрузить участника')));
  }, [groupId, memberId]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const addSkill = () => {
    const title = skillInput.trim();
    if (!title) return;
    if (!form.skills.some((s) => s.title.toLowerCase() === title.toLowerCase())) {
      setField('skills', [...form.skills, { title }]);
    }
    setSkillInput('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!form.name.trim() || !form.surname.trim()) {
      toast.error('Введите имя и фамилию');
      return;
    }
    if (!isEdit && !mainImage) {
      toast.error('Загрузите фото участника');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      const model = {
        ...form,
        imgPath: undefined,
        cvFilepath: undefined,
        teamGroupId: Number(groupId),
        skills: form.skills.filter((s) => s.title.trim()),
      };
      const preparedLocalizations = localizations.map((l) => ({
        ...l,
        detailContent: l.detailContent || { items: [] },
        skills: (l.skills || [])
          .filter((s) => s.title?.trim())
          .map((s) => ({ title: s.title.trim() })),
      }));
      const body = buildEntityFormData({
        model,
        mainImage,
        contentKeys: CONTENT_KEYS,
        localizations: preparedLocalizations,
      });
      if (cvFile) {
        body.append('cvFile', cvFile);
      }

      if (isEdit) {
        await apiRequests.team.updateMember(body);
        toast.success('Участник сохранён');
      } else {
        await apiRequests.team.createMember(body);
        toast.success('Участник добавлен');
        setTimeout(() => navigate(backTo), 1000);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить участника'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate(backTo)}
      backText="← Назад к группе"
      saving={saving}
      submitText={isEdit ? 'Сохранить изменения' : 'Добавить участника'}
      savingText={isEdit ? 'Сохранение...' : 'Создание...'}
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <Row>
              <Col md={6}>
                <FormField label="Имя" required>
                  <Form.Control
                    type="text"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Фамилия" required>
                  <Form.Control
                    type="text"
                    value={form.surname}
                    onChange={(e) => setField('surname', e.target.value)}
                  />
                </FormField>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormField label="Должность">
                  <Form.Control
                    type="text"
                    value={form.position}
                    onChange={(e) => setField('position', e.target.value)}
                    placeholder="Например, UI/UX-дизайнер"
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Опыт" hint="Коротко, для карточки">
                  <Form.Control
                    type="text"
                    value={form.shortExpierence}
                    onChange={(e) => setField('shortExpierence', e.target.value)}
                    placeholder="Например, 5 лет"
                  />
                </FormField>
              </Col>
            </Row>
            <FormField label="Цитата">
              <Form.Control
                as="textarea"
                rows={3}
                value={form.quote}
                onChange={(e) => setField('quote', e.target.value)}
              />
            </FormField>
            <FormField
              label="Резюме"
              hint={
                form.cvFilepath
                  ? 'Загрузите новый файл, чтобы заменить текущее резюме'
                  : 'Необязательно. PDF или Word'
              }
            >
              <UploadPicture
                id="team-member-cv"
                onSelect={setCvFile}
                initialValue={form.cvFilepath || undefined}
                accept={CV_ACCEPT}
                label="Загрузка файла"
              />
            </FormField>
          </Col>

          <Col lg={4}>
            <FormField label="Фото" required={!isEdit}>
              <UploadPicture
                onSelect={setMainImage}
                initialValue={form.imgPath || undefined}
                entityType="team"
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Навыки">
        <div className="d-flex mb-3" style={{ maxWidth: 420 }}>
          <Form.Control
            type="text"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addSkill();
              }
            }}
            placeholder="Например, Figma — и Enter"
            className="me-2"
          />
          <Button variant="outline-secondary" onClick={addSkill} title="Добавить навык">
            <Plus size={16} />
          </Button>
        </div>
        {form.skills.length > 0 ? (
          <div className="d-flex flex-wrap gap-2">
            {form.skills.map((skill, index) => (
              <span
                key={`${skill.title}-${index}`}
                className="badge bg-light text-dark border d-inline-flex align-items-center p-2"
              >
                {skill.title}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 ms-2 text-secondary"
                  title="Убрать навык"
                  onClick={() =>
                    setField(
                      'skills',
                      form.skills.filter((_, i) => i !== index),
                    )
                  }
                >
                  <X size={14} />
                </Button>
              </span>
            ))}
          </div>
        ) : (
          <Form.Text className="text-muted">
            Навыки показываются тегами на странице сотрудника.
          </Form.Text>
        )}
      </FormSection>

      <FormSection title="Подробно о сотруднике">
        <ContentEditor
          key={form.id || 'new'}
          value={form.detailContent.items}
          onChange={(items) => setField('detailContent', { items })}
        />
      </FormSection>

      <FormSection title="SEO">
        <SEOMetatagsForm
          seoMetatags={form.seoMetatags}
          onChange={(seo) => setField('seoMetatags', seo)}
        />
      </FormSection>

      <LocalizationsBlock
        value={localizations}
        onChange={setLocalizations}
        fields={LOCALIZATION_FIELDS}
        titleField="name"
        source={form}
        aiContext="карточка сотрудника команды; имена и фамилии транслитерируй"
      />
    </FormLayout>
  );
};

export default TeamMemberFormPage;
